import React from 'react';
import { useTranslation } from 'react-i18next';

import { Event, Rate } from '../../../../../../../models';

import { SelectHierarchyLabel } from '@weezevent/nacre';

import css from '../../conditions.module.css';

const RateFilterCondition = ({ organizationId, flowId, condition, handleCondition }) => {
    const { t } = useTranslation();

    const [selectedEvent, setSelectedEvent] = React.useState(() => condition.rates[0]?.event);
    const [selectedRates, setSelectedRates] = React.useState(() => condition.rates?.map(r => r.id));

    const [events, loadingEvents] = Event.useApiModel(
        {
            organizationId,
            flowId,
            cache: false,
            allow_cache: false,
            query: {
                moderation_flow_id__in: flowId
            },
            onSuccess: response => {
                if (response.length === 1) {
                    setSelectedEvent(response[0].pk);
                }
            }
        },
        [flowId, organizationId]
    );

    const [rates, loadingRates] = Rate.useApiModel(
        {
            organizationId,
            eventId: selectedEvent,
            cache: false,
            allow_cache: false,
            launch: Boolean(selectedEvent)
        },
        [selectedEvent, organizationId]
    );

    const eventsOptions = React.useMemo(() => {
        if (!events || loadingEvents) {
            return [];
        }

        return events.map(event => {
            return event.toOptions();
        });
    }, [events, loadingEvents]);

    const ratesOptions = React.useMemo(() => {
        if (!rates || loadingRates) {
            return [];
        }

        return rates.map(rate => ({
            text: rate.rate_template?.name ?? rate.name,
            value: rate.id,
            item: rate.object
        }));
    }, [rates, loadingRates]);

    const selectedRatesLabel = React.useMemo(() => {
        if (!selectedRates) {
            return [];
        }
        const rates = ratesOptions?.filter(r => selectedRates.includes(r.value));

        if (!rates.length) {
            return null;
        }
        if (rates.length !== ratesOptions?.length) {
            return t('ticket.moderation.flows.create.ratesSelectedCount', { count: rates.length });
        }
        return t('ticket.moderation.flows.create.eventsAllSelected');
    }, [ratesOptions, selectedRates]);

    const handleChangeRates = React.useCallback(
        values => {
            setSelectedRates(values);
            handleCondition(
                'rates',
                ratesOptions.filter(r => values.includes(r.value)).map(r => r.item)
            );
        },
        [ratesOptions, handleCondition]
    );

    return (
        <>
            <div className={css['selector-container']}>
                <SelectHierarchyLabel
                    value={selectedEvent}
                    options={eventsOptions}
                    loading={loadingEvents}
                    disabled={loadingEvents}
                    selected={selectedEvent}
                    onChange={value => setSelectedEvent(value)}
                    placeholder={t('ticket.common.action.select')}
                    label={t('ticket.moderation.statusesTransitions.conditions.detail.conditionRules.rate-filter.events.label')}
                />

                <SelectHierarchyLabel
                    multi
                    loading={loadingRates}
                    options={ratesOptions}
                    selected={selectedRates}
                    value={selectedRatesLabel}
                    onChange={handleChangeRates}
                    disabled={!selectedEvent || loadingRates}
                    placeholder={t('ticket.common.action.select')}
                    label={t('ticket.moderation.statusesTransitions.conditions.detail.conditionRules.rate-filter.rates.label')}
                />
            </div>
        </>
    );
};

export default RateFilterCondition;
